import { useRef, ComponentProps } from "react";
import { Flex, Icon, LabelWrapper, OverflowText, Tooltip } from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import { SelectableSearchMenu } from "@/appComponents/Blocks/Menus/BasicChartSearchMenu/SelectableSearchMenu";
import { FieldInputValuePickerComposite } from "@/appComponents/Blocks/Composites/FieldInputValuePickerComposite/FieldInputValuePickerComposite";
import { FormatPositionLabel } from "@coreapp/utils/FormatPositionLabel";
import { QObjDBObject } from "@coreapp/CoreApp/QObjDBObject";

interface Field {
  name: string;
  value: any;
  textEditOptions?: {
    blankMsg?: string;
    enableTE?: boolean;
    disableTE?: boolean;
    order?: number;
    row?: number;
    width?: string;
    showTrigger?: boolean;
    inputProps?: any;
    isSupervisorField?: boolean;
    supervisorFieldProps?: any;
  };
  fieldDef?: any;
  error?: boolean;
  warning?: boolean;
  revertButton?: React.ReactNode;
  multiValueIndicator?: string;
  individualValues?: string;
  valuesListTooltip?: React.ReactNode;
}

interface FieldValuesMap {
  [key: string]: Field;
}

interface RangeField {
  min: Field;
  max: Field;
}

interface RangeFieldsMap {
  [key: string]: RangeField;
}

type BasicFieldInputProps = {
  field: Field;
  onChange: Function;
  showLabel?: boolean;
  dataTestIdPrefix?: string;
  children?: React.ReactNode;
  queryChartId?: string | null;
};

const FieldMapToRows = (fieldMap: FieldValuesMap) => {
  const fieldKeys = Object.keys(fieldMap);

  const orderedFields = fieldKeys.map(key => {
    const field = fieldMap[key];
    const options = field.textEditOptions || {};

    return {
      key,
      field,
      order: options.order || 99,
      row: options.row || 99
    };
  });

  orderedFields.sort((a, b) => {
    if (a.row !== b.row) return a.row - b.row;
    return a.order - b.order;
  });

  const fieldsByRow: { [key: number]: typeof orderedFields } = {};
  orderedFields.forEach(item => {
    const row = item.row || 0;
    if (!fieldsByRow[row]) fieldsByRow[row] = [];
    fieldsByRow[row].push(item);
  });

  return fieldsByRow;
};

const BasicFieldInput = ({
  field,
  onChange,
  showLabel = true,
  dataTestIdPrefix,
  children,
  queryChartId = null
}: BasicFieldInputProps) => {
  const fieldName = field.name;
  const value = field.value;
  const options = field.textEditOptions || {};
  const fieldDef = field.fieldDef;
  const error = field?.error;
  const warning = field?.warning;
  const individualValues = field?.individualValues;
  const valuesListTooltip = field?.valuesListTooltip;

  return (
    <Flex direction="column" gap="4px">
      <FieldInputValuePickerComposite
        field={fieldName}
        value={value}
        onChange={onChange}
        dataTestIdPrefix={`${dataTestIdPrefix}-field`}
        inputProps={{
          status: error ? "error" : warning ? "warning" : null,
          message: "",
          placeholder: options?.blankMsg || "<Blank>",
          disabled: !options?.enableTE,
          size: "medium",
          labelSize: "medium",
          modifier: "medium",
          className: "ocn-plan-explorer-create-plan",
          ...(options?.inputProps || {})
        }}
        triggerProps={{ disabled: options?.disableTE, triggerModifier: "medium" }}
        showTrigger={options?.showTrigger || false}
        showPickListWarning={false}
        showLabel={showLabel}
        parentFieldDef={fieldDef}
        popoverRef={undefined}
        menuProps={undefined}
        mode={undefined}
        selectedItem={undefined}
        onRemoveItem={undefined}
        queryChartId={queryChartId as any}
      >
        {children}
      </FieldInputValuePickerComposite>
      {individualValues && (
        <Flex alignItems="center" gap="4px">
          {valuesListTooltip && (
            <Tooltip content={valuesListTooltip}>
              <div className="oc-budget-info-icon">
                <Icon name="circle-info" size="small" />
              </div>
            </Tooltip>
          )}
          <OverflowText size="xsmall">{individualValues}</OverflowText>
        </Flex>
      )}
    </Flex>
  );
};

type SupervisorFieldInputProps = {
  field: Field;
  clickHandler: (target: any) => void;
  searchHandleRef: React.RefObject<any>;
  dataTestIdPrefix?: string;
  label?: string;
  labelWrapperProps?: ComponentProps<typeof LabelWrapper>;
  selectableSearchMenuProps?: ComponentProps<typeof SelectableSearchMenu>;
};

export const SupervisorFieldInput = ({
  label,
  field,
  clickHandler,
  searchHandleRef,
  dataTestIdPrefix,
  labelWrapperProps,
  selectableSearchMenuProps
}: SupervisorFieldInputProps) => {
  const options = field.textEditOptions || {};

  return (
    <LabelWrapper label={appstr("Reports to")} require={true} {...labelWrapperProps}>
      <SelectableSearchMenu
        dataTestId={`${dataTestIdPrefix}-supervisor-search`}
        inputSize={"medium"}
        displayRecents={false}
        selectedItem={{ label: label ?? field?.value, value: field?.value }}
        onRemoveItem={() => {}}
        searchMenuProps={{
          clearOnSelect: true,
          clearable: true,
          placeholder: "",
          expandWidth: true,
          searchProps: {
            handleRef: searchHandleRef
          },
          inputProps: {
            disabled: !options?.enableTE
          }
        }}
        handleSearchOptionClick={(target: any) => clickHandler(target)}
        queryOptions={{ includeAux: false }}
        {...selectableSearchMenuProps}
      />
    </LabelWrapper>
  );
};

type RangeInputsProps = {
  fieldKey: string;
  minField: Field;
  maxField: Field;
  onChange: Function;
  dataTestIdPrefix?: string;
  queryChartId?: string | null;
};

const RangeInputs = ({
  fieldKey,
  minField,
  maxField,
  onChange,
  dataTestIdPrefix,
  queryChartId = null
}: RangeInputsProps) => {
  const processedMinField = {
    ...minField,
    revertButton: null,
    textEditOptions: {
      ...minField.textEditOptions,
      inputProps: {
        ...minField.textEditOptions?.inputProps,
        postfixNode: minField.revertButton
      }
    }
  };

  const processedMaxField = {
    ...maxField,
    revertButton: null,
    textEditOptions: {
      ...maxField.textEditOptions,
      inputProps: {
        ...maxField.textEditOptions?.inputProps,
        postfixNode: maxField.revertButton
      }
    }
  };

  return (
    <LabelWrapper label={appstr(fieldKey)} labelInfo={appstr(`${fieldKey}info`)}>
      <Flex direction={"row"} gap={"8px"} alignItems={"center"}>
        {`${appstr("min").toLowerCase()}:`}
        <BasicFieldInput
          field={processedMinField}
          onChange={onChange}
          showLabel={false}
          dataTestIdPrefix={dataTestIdPrefix}
          queryChartId={queryChartId}
        />
        <Icon name="minus" size={"small"} />
        {`${appstr("max").toLowerCase()}:`}
        <BasicFieldInput
          field={processedMaxField}
          onChange={onChange}
          showLabel={false}
          dataTestIdPrefix={dataTestIdPrefix}
          queryChartId={queryChartId}
        />
      </Flex>
    </LabelWrapper>
  );
};

type BasicInfoSectionProps = {
  fieldValuesMap: FieldValuesMap;
  rangeFieldsMap?: RangeFieldsMap;
  onFieldChange: (field: any) => void;
  onSearchSelect: (target: any) => void;
  dataTestIdPrefix?: string;
  parentObject?: QObjDBObject;
  padding?: string;
  isEditable?: boolean;
  queryChartId?: string | null;
};

const BasicInfoSection = ({
  fieldValuesMap,
  rangeFieldsMap,
  onFieldChange,
  onSearchSelect,
  dataTestIdPrefix,
  parentObject,
  padding = "4px 16px 0px",
  queryChartId = null
}: BasicInfoSectionProps) => {
  if (!fieldValuesMap) {
    return null;
  }

  const searchHandleRef = useRef(null);

  const fieldsByRow = FieldMapToRows(fieldValuesMap);

  const sortedRows = Object.keys(fieldsByRow).sort((a, b) => Number(a) - Number(b));

  return (
    <Flex
      className={"ocn-list-scrollbar"}
      direction={"column"}
      padding={padding}
      gap={"16px"}
      height={"100%"}
      overflow={"auto"}
      dataTestId={`${dataTestIdPrefix}-basic-info-section`}
      width={"100%"}
    >
      {sortedRows.map(rowKey => {
        const rowFields = fieldsByRow[Number(rowKey)];

        rowFields.sort((a: { order: number }, b: { order: number }) => a.order - b.order);

        return (
          <Flex key={`row-${rowKey}`} gap={"16px"} alignItems={"flex-end"}>
            {rowFields.map(({ key, field }: { key: string; field: Field }) => {
              const options = field.textEditOptions || {};

              if (options.isSupervisorField) {
                const searchClass = field.warning ? "oc-special-search" : "";
                return (
                  <div
                    key={key}
                    className={"oc-position-editor-search"}
                    style={{ width: options.width || "50%" }}
                  >
                    <Flex direction="column" gap="4px">
                      <Flex gap={"8px"} alignItems={"flex-end"} className={searchClass}>
                        <div style={{ flexGrow: 1 }}>
                          <SupervisorFieldInput
                            label={
                              options.supervisorFieldProps?.selectableSearchMenuProps?.selectedItem
                                ?.label ||
                              (parentObject ? FormatPositionLabel(parentObject) : undefined)
                            }
                            field={field}
                            clickHandler={target =>
                              onSearchSelect({ ...target, field: field.name })
                            }
                            searchHandleRef={searchHandleRef}
                            dataTestIdPrefix={dataTestIdPrefix}
                            selectableSearchMenuProps={
                              options.supervisorFieldProps?.selectableSearchMenuProps
                            }
                            labelWrapperProps={options.supervisorFieldProps?.labelWrapperProps}
                          />
                        </div>
                        {field.revertButton}
                      </Flex>
                      {field.individualValues && (
                        <Flex alignItems="center" gap="4px">
                          {field.valuesListTooltip && (
                            <Tooltip content={field.valuesListTooltip}>
                              <div className="oc-budget-info-icon">
                                <Icon name="circle-info" size="small" />
                              </div>
                            </Tooltip>
                          )}
                          <OverflowText size="xsmall">{field.individualValues}</OverflowText>
                        </Flex>
                      )}
                    </Flex>
                  </div>
                );
              }

              return (
                <div key={key} style={{ width: options.width || "50%" }}>
                  <BasicFieldInput
                    field={field}
                    onChange={onFieldChange}
                    dataTestIdPrefix={dataTestIdPrefix}
                    queryChartId={queryChartId}
                  >
                    {field.revertButton}
                  </BasicFieldInput>
                </div>
              );
            })}
          </Flex>
        );
      })}

      {rangeFieldsMap && Object.keys(rangeFieldsMap).length > 0 && (
        <Flex direction="column" gap="16px">
          {Object.keys(rangeFieldsMap).map(key => {
            const { min, max } = rangeFieldsMap[key];
            return (
              <RangeInputs
                key={key}
                fieldKey={key}
                minField={min}
                maxField={max}
                onChange={onFieldChange}
                dataTestIdPrefix={dataTestIdPrefix}
                queryChartId={queryChartId}
              />
            );
          })}
        </Flex>
      )}
    </Flex>
  );
};

export default BasicInfoSection;
